import React, { useState, useEffect } from 'react';
import {
  DialogTitle, DialogContent, DialogActions, Button, Typography, Autocomplete,
  TextField, CircularProgress, Alert, TableContainer, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, Checkbox, Chip, Tooltip, IconButton, Link,
} from '@mui/material';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import axios from 'axios';
import { apiUrl, handleApiError } from '../utils/api';
import { useNotify } from './NotificationProvider';
import AppDialog from './AppDialog';

// Shared "Merge customer names" dialog — maps OCR spelling variants of one customer to a single
// canonical name so the field-ticket invoice and customer list group them together. Read-time
// resolution (does NOT rewrite the ticket's stored text); new scans of a mapped spelling merge
// automatically. Backed by the existing /api/field-tickets/customer-{variants,aliases} endpoints.
const base = `${apiUrl}/api/field-tickets`;

// Must match FieldCustomerService.normalize (upper-case + strip non-alphanumerics) so a variant's
// alias id can be found from its raw spelling (for un-mapping).
const normKey = (s) => String(s ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

export default function CustomerMergeDialog({ open, onClose, email, onChanged }) {
  const notify = useNotify();
  const [variants, setVariants] = useState([]);  // [{raw, canonical, count, mapped}]
  const [aliases, setAliases] = useState([]);    // [{id, sourceKey, canonicalName}]
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [picked, setPicked] = useState(() => new Set()); // selected raw variant strings
  const [target, setTarget] = useState('');      // canonical name to merge into
  const [changed, setChanged] = useState(false); // any merge/unmap this session → refetch parent on close

  // Refresh the parent's grid ONCE, on close, and only if something actually changed — instead of
  // after every merge/unmap (which would flicker the grid behind the still-open dialog).
  const handleClose = () => {
    if (changed && onChanged) onChanged();
    setChanged(false);
    onClose();
  };

  const loadVariants = async () => {
    setLoading(true);
    try {
      const [vRes, aRes] = await Promise.allSettled([
        axios.get(`${base}/customer-variants`),
        axios.get(`${base}/customer-aliases`),
      ]);
      setVariants(vRes.status === 'fulfilled' && Array.isArray(vRes.value.data) ? vRes.value.data : []);
      setAliases(aRes.status === 'fulfilled' && Array.isArray(aRes.value.data) ? aRes.value.data : []);
    } catch (err) {
      notify.error(handleApiError(err, 'Failed to load customer names'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) { setPicked(new Set()); setTarget(''); setChanged(false); loadVariants(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const togglePick = (raw) => setPicked((prev) => {
    const n = new Set(prev);
    if (n.has(raw)) n.delete(raw); else n.add(raw);
    return n;
  });

  // Canonical suggestions: existing canonicals first, then every raw spelling (promote a good one).
  const canonicalOptions = Array.from(new Set([
    ...aliases.map((a) => a.canonicalName).filter(Boolean),
    ...variants.map((v) => v.raw).filter(Boolean),
  ]));

  const doMerge = async () => {
    const canon = target.trim();
    if (!canon) { notify.warning('Choose the customer to merge into.'); return; }
    const sources = [...picked].filter((raw) => normKey(raw) !== normKey(canon)); // never map onto itself
    if (sources.length === 0) { notify.warning('Select at least one different spelling to merge.'); return; }
    setMerging(true);
    const results = await Promise.allSettled(sources.map((raw) =>
      axios.post(`${base}/customer-aliases`, { sourceName: raw, canonicalName: canon, email })));
    setMerging(false);
    const failed = results.filter((r) => r.status === 'rejected').length;
    if (failed === 0) notify.success(`Merged ${sources.length} spelling(s) into "${canon}".`);
    else notify.warning(`Merged ${sources.length - failed} of ${sources.length}; ${failed} failed.`);
    if (failed < sources.length) setChanged(true); // refetch parent grid once, on close
    setPicked(new Set());
    await loadVariants(); // in-dialog list only — reflects the new mappings while the dialog stays open
  };

  const unmap = async (raw) => {
    const hit = aliases.find((a) => a.sourceKey === normKey(raw));
    if (!hit) return;
    try {
      await axios.delete(`${base}/customer-aliases/${hit.id}`);
      notify.success(`Unmapped "${raw}".`);
      setChanged(true); // refetch parent grid once, on close
      await loadVariants();
    } catch (err) {
      notify.error(handleApiError(err, 'Unmap failed'));
    }
  };

  // Unmapped variants first (need attention), then most frequent — highest-impact merges on top.
  const sortedVariants = [...variants].sort((a, b) =>
    (a.mapped === b.mapped ? (b.count || 0) - (a.count || 0) : a.mapped ? 1 : -1));

  return (
    <AppDialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Merge customer names</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Handwritten tickets read the same customer many ways. Pick the correct name, tick every
          spelling that means the same customer, and merge them — invoices and the customer list will
          then group them as one. The ticket's original text is preserved, and new scans of a mapped
          spelling merge automatically.
        </Typography>

        <Autocomplete
          freeSolo options={canonicalOptions} value={target}
          onChange={(e, v) => setTarget(v || '')} onInputChange={(e, v) => setTarget(v || '')}
          renderInput={(p) => (
            <TextField {...p} size="small" label="Merge into (canonical customer)" placeholder="e.g. Williams Brothers" />
          )}
          sx={{ mb: 2 }}
        />

        {loading && <CircularProgress size={22} />}
        {!loading && variants.length === 0 && (
          <Alert severity="info">No customer names yet — scan some field tickets first.</Alert>
        )}
        {!loading && variants.length > 0 && (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Spelling on tickets</TableCell>
                  <TableCell align="right">Tickets</TableCell>
                  <TableCell>Currently groups as</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedVariants.map((v) => (
                  <TableRow key={v.raw} hover selected={picked.has(v.raw)}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={picked.has(v.raw)} onChange={() => togglePick(v.raw)} />
                    </TableCell>
                    <TableCell>{v.raw}</TableCell>
                    <TableCell align="right">{v.count}</TableCell>
                    <TableCell>
                      {v.mapped
                        ? <Chip size="small" color="success" variant="outlined" label={v.canonical} />
                        : <Typography variant="caption" color="text.secondary">(itself)</Typography>}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Use this spelling as the canonical name">
                        <IconButton size="small" onClick={() => setTarget(v.raw)}><CallMergeIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      {v.mapped && (
                        <Tooltip title="Unmap (stop merging this spelling)">
                          <IconButton size="small" color="error" onClick={() => unmap(v.raw)}><LinkOffIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && variants.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {picked.size} selected{target.trim() ? ` → will merge into "${target.trim()}"` : ''}.{' '}
            <Link component="button" type="button" underline="hover"
              onClick={() => setPicked(new Set(sortedVariants.filter((v) => !v.mapped).map((v) => v.raw)))}>
              Select all unmapped
            </Link>
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
        <Button variant="contained" startIcon={<CallMergeIcon />} disabled={merging || loading} onClick={doMerge}>
          {merging ? 'Merging…' : `Merge ${picked.size || ''} selected`}
        </Button>
      </DialogActions>
    </AppDialog>
  );
}
